import { useQueries } from '@tanstack/react-query';
import { getInspections, InspectionStatus } from '../lib/inspections';

/**
 * 점검 목록 상단 통계 카드의 값.
 *
 * 문서관리·결재함은 전용 통계 엔드포인트(`/reports/stats` 등)에서 이 수를 받지만, 점검에는
 * 아직 그런 엔드포인트가 없다. 그래서 **기존 목록 엔드포인트를 `limit=1`로 불러 `meta.total`만
 * 읽는다** — 행은 한 건도 받지 않으므로 사실상 count 질의다. 통계 전용 API가 생기면 이 훅의
 * 속만 갈아끼우면 되도록 소비처에는 수치 4개만 노출한다.
 */
export interface InspectionStats {
  /** 상태를 가리지 않은 전체 — 목록의 `totalCount`와 다르다(그쪽은 상태 필터가 걸린 수다). */
  total: number;
  inProgress: number;
  pendingReview: number;
  completed: number;
}

/**
 * 통계가 세는 범위 — 목록에 걸린 필터 중 **상태를 뺀 나머지**다.
 *
 * 상태를 빼는 건 카드마다 상태가 다르기 때문이고, 나머지를 그대로 물려받는 건 카드 수와 목록
 * 수가 갈리지 않게 하기 위해서다(현장을 고르면 카드도 그 현장 기준이어야 "완료 3건"을 눌러
 * 3행이 나온다). `includeDeleted`도 같은 이유로 함께 따라간다.
 */
export interface InspectionStatsScope {
  siteId?: string;
  locationId?: string;
  equipmentId?: string;
  includeDeleted?: boolean;
  /** 기간 시작 (현지 YYYY-MM-DD) — 목록과 같은 범위를 세기 위해 함께 따라간다. */
  startDate?: string;
  /** 기간 종료 (현지 YYYY-MM-DD). */
  endDate?: string;
}

/** 카드 4장의 순서 = 질의 순서. `undefined`는 "상태를 가리지 않음"(전체 카드)이다. */
const STAT_STATUSES: (InspectionStatus | undefined)[] = [
  undefined,
  InspectionStatus.IN_PROGRESS,
  InspectionStatus.PENDING_REVIEW,
  InspectionStatus.COMPLETED,
];

export function useInspectionStats(
  companyId: string | undefined,
  scope: InspectionStatsScope
): { stats: InspectionStats | null; isLoading: boolean } {
  return useQueries({
    queries: STAT_STATUSES.map((status) => ({
      queryKey: [
        'inspections',
        'stats',
        companyId,
        status ?? 'all',
        scope.siteId ?? null,
        scope.locationId ?? null,
        scope.equipmentId ?? null,
        scope.includeDeleted ?? false,
        scope.startDate ?? null,
        scope.endDate ?? null,
      ],
      queryFn: async () => {
        const response = await getInspections(companyId!, {
          ...scope,
          status,
          // 수만 필요하므로 행은 받지 않는다. limit=0은 서버가 "미지정"으로 볼 수 있어 1로 둔다.
          limit: 1,
        });
        return response.meta?.total ?? 0;
      },
      enabled: !!companyId,
    })),
    combine: (results) => ({
      // 한 장이라도 못 읽으면 통계를 통째로 비운다 — 일부만 0으로 채우면 카드끼리
      // 앞뒤가 안 맞는 수를 보여 주게 된다(소비처는 null을 '—'로 낸다).
      stats: results.every((r) => r.isSuccess)
        ? {
            total: results[0].data ?? 0,
            inProgress: results[1].data ?? 0,
            pendingReview: results[2].data ?? 0,
            completed: results[3].data ?? 0,
          }
        : null,
      isLoading: results.some((r) => r.isLoading),
    }),
  });
}

export default useInspectionStats;
